import * as THREE from 'three';
import { ALU_THRESHOLD_SECTION_MM, isLegacyAluSection } from './aluThreshold.js';
import { sampleProfile } from './profileGeometry.js';
import { mapExtrudedWoodUVs } from './woodUV.js';
import { assemblyClearances } from './assemblyClearance.js';

// Measured 60 mm system jamb section: [inset from outer frame edge, Z], mm.
// The head spans square-cut jambs; its raised rebate is notched at both ends.
export const FRAME_PROFILE_MM = [
  [38,-5.5],[27,-5.5],[27,-50.5],[26.9,-51.12],[26.62,-51.68],
  [26.18,-52.12],[25.62,-52.4],[25,-52.5],[2,-52.5],[1.38,-52.4],
  [0.82,-52.12],[0.38,-51.68],[0.1,-51.12],[0,-50.5],[0,63.5],
  [0.1,64.12],[0.38,64.68],[0.82,65.12],[1.38,65.4],[2,65.5],
  [38,65.5],[38.62,65.4],[39.18,65.12],[39.62,64.68],[39.9,64.12],
  [40,63.5],[40,-3.5],[39.9,-4.12],[39.62,-4.68],[39.18,-5.12],[38.62,-5.4]
];

// Sections measured from the current GLB; runtime geometry is generated here.
// Threshold coordinates: [Z, height above floor], mm. Pine uses the matching
// timber rebate, oak the sloped sill, aluminium the shared approved reference section.
const OAK_SECTION = [
  [-65.4,9.62],[-65.12,10.18],[-64.68,10.62],[-64.12,10.9],[-63.5,11],
  [50.5,11],[51.12,10.9],[51.68,10.62],[52.12,10.18],[52.4,9.62],[52.5,9],
  [52.5,4.42],[52.41,3.81],[52.14,3.27],[51.72,2.83],[51.18,2.54],[50.59,2.42],
  [5.5,0],[5.5,-9],[5.4,-9.62],[5.12,-10.18],[4.68,-10.62],[4.12,-10.9],
  [3.5,-11],[-63.5,-11],[-64.12,-10.9],[-64.68,-10.62],[-65.12,-10.18],
  [-65.4,-9.62],[-65.5,-9],[-65.5,9]
].map(([y,z]) => [-y, 11-z]);
export const THRESHOLDS = {
  threshold_oak: { label: 'Oak', material: 'oak', leafBottomMm: 14, profiles: [OAK_SECTION] },
  threshold_pine: { label: 'Pine', material: 'pine', leafBottomMm: 29, profiles: [FRAME_PROFILE_MM.map(([x,z]) => [z,x])] },
  threshold_alu: { label: 'Aluminium', material: 'alu', leafBottomMm: 15.4, profiles: [ALU_THRESHOLD_SECTION_MM] }
};
export const thresholdSpec = (key, overrides) => {
  const base = THRESHOLDS[key] || THRESHOLDS.threshold_oak;
  const custom = overrides?.[key];
  const profiles = key==='threshold_alu' && isLegacyAluSection(custom?.profiles)
    ? base.profiles : custom?.profiles ?? base.profiles;
  return { ...base, profiles, leafBottomMm: custom?.leafBottomMm ?? base.leafBottomMm };
};
export const frameSpec = (frame) => ({ profileMm: frame?.profileMm ?? FRAME_PROFILE_MM, leafInsetMm: frame?.leafInsetMm ?? 29 });
export const RADIUS_FRAME_PROFILE_MM = [[27,-5.5,0],[27,-52.5,2],[0,-52.5,2],[0,65.5,2],[40,65.5,2],[40,-5.5,2]];
export const frameDimensions = (design) => {
  const gap = assemblyClearances(design);
  return { widthMm: design.widthMm + 2 * gap.sideMm, heightMm: design.heightMm + gap.topMm + gap.bottomMm };
};

function section(points, label, frame = false) {
  const sampled = sampleProfile(points, { closed: true });
  if (sampled.some(([x,y]) => frame ? x < 0 || x > 200 || Math.abs(y) > 300 : Math.abs(x) > 300 || y < 0 || y > 150)) {
    throw new Error(`${label}: frame width 0–200 mm, depth ±300 mm; threshold height 0–150 mm.`);
  }
  const area = sampled.reduce((a,p,i) => { const n = sampled[(i+1)%sampled.length]; return a+p[0]*n[1]-n[0]*p[1]; },0);
  if (Math.abs(area) < 0.01) throw new Error(`${label}: cross-section must have a non-zero area.`);
  return sampled;
}

export function validateFrameDesign(config = {}) {
  const frame = frameSpec(config.frame);
  if (!Number.isFinite(frame.leafInsetMm) || frame.leafInsetMm < 1 || frame.leafInsetMm > 200) throw new Error('Leaf inset into frame: 1–200 mm.');
  const profileMm = section(frame.profileMm, 'Frame', true);
  if (config.thresholds && (typeof config.thresholds !== 'object' || Array.isArray(config.thresholds) ||
      Object.keys(config.thresholds).some((key) => !THRESHOLDS[key]))) throw new Error('Threshold profile keys: threshold_oak, threshold_pine, threshold_alu.');
  const thresholds = {};
  for (const key of new Set([config.threshold || 'threshold_oak', ...Object.keys(config.thresholds || {})])) {
    const spec = thresholdSpec(key, config.thresholds);
    if (!Number.isFinite(spec.leafBottomMm) || spec.leafBottomMm < 0 || spec.leafBottomMm > 200) throw new Error('Leaf bottom must be 0–200 mm above the floor.');
    if (!Array.isArray(spec.profiles) || spec.profiles.length < 1 || spec.profiles.length > 12) throw new Error('Threshold requires 1–12 closed contours.');
    thresholds[key] = { ...spec, profiles: spec.profiles.map((p,i) => section(p, `${spec.label}, kontuur ${i+1}`)) };
  }
  return { frame: { ...frame, profileMm }, threshold: thresholds[config.threshold || 'threshold_oak'] };
}

function ccw(path) {
  const area = path.reduce((sum, p, i) => {
    const n = path[(i + 1) % path.length]; return sum + p[0] * n[1] - n[0] * p[1];
  }, 0);
  return area > 0 ? path : [...path].reverse();
}

export function geometryOf(solid) {
  if (solid.isEmpty() || solid.status() !== 'NoError') throw new Error('Could not build frame or threshold geometry.');
  const mesh = solid.getMesh();
  const indexed = new THREE.BufferGeometry();
  indexed.setAttribute('position', new THREE.Float32BufferAttribute(mesh.vertProperties, 3));
  indexed.setIndex(Array.from(mesh.triVerts));
  const geometry = indexed.toNonIndexed(); indexed.dispose();
  geometry.scale(0.001, 0.001, 0.001); geometry.computeVertexNormals();
  const p = geometry.getAttribute('position'), n = geometry.getAttribute('normal');
  const uv = new Float32Array(p.count * 2);
  for (let i = 0; i < p.count; i++) {
    uv[i * 2] = Math.abs(n.getX(i)) > 0.5 ? p.getZ(i) : p.getX(i);
    uv[i * 2 + 1] = Math.abs(n.getY(i)) > 0.5 ? p.getZ(i) : p.getY(i);
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geometry.computeBoundingBox(); geometry.computeBoundingSphere();
  return geometry;
}

export function createDoorFrameBuilder(Manifold) {
  const cache = new Map();
  return (config) => {
    const { widthMm: w, heightMm: h } = config;
    if (![w,h].every((v) => Number.isFinite(v) && v > 80)) throw new Error('Frame dimension must be greater than 80 mm.');
    const { frame, threshold: spec } = validateFrameDesign(config);
    const maxInset = Math.max(...frame.profileMm.map((p) => p[0]));
    // Straight depth-facing edges identify the stock thickness below the rebate
    // (27 mm on the standard 40 mm profile). A plain section uses its full width.
    const stockThickness = Math.min(maxInset, ...frame.profileMm.flatMap((a, i) => {
      const b = frame.profileMm[(i + 1) % frame.profileMm.length];
      return a[0] > 1e-6 && Math.abs(a[0] - b[0]) < 1e-6 && Math.abs(a[1] - b[1]) > 1e-6 ? [a[0]] : [];
    }));
    if (w <= 2 * maxInset || h <= maxInset + Math.max(...spec.profiles.flat().map((p) => p[1]))) throw new Error('Frame dimensions are too small for the selected profiles.');
    const key = JSON.stringify([w,h,frame.profileMm,spec.material,spec.profiles]);
    if (cache.has(key)) return cache.get(key).map((p) => ({ ...p, geometry: p.geometry.clone() }));
    const owned = [], output = [];
    const keep = (s) => { owned.push(s); return s; };
    const transform = (s, x, y, z, position = [0,0,0]) => {
      const matrix = new THREE.Matrix4().makeBasis(new THREE.Vector3(...x), new THREE.Vector3(...y), new THREE.Vector3(...z));
      matrix.setPosition(...position); return keep(s.transform(matrix.elements));
    };
    try {
      const sill = transform(keep(Manifold.extrude(spec.profiles.map(ccw), w)), [0,0,1], [0,1,0], [1,0,0]);
      // Project every threshold edge down to the floor. This cuts the jamb to
      // the actual top envelope, keeping timber out of aluminium's cavities too.
      const envelope = spec.profiles.flatMap((path) => path.flatMap((a, i) => {
        const b = path[(i + 1) % path.length];
        if (Math.abs(a[0] - b[0]) < 1e-6) return [];
        const [l,r] = a[0] < b[0] ? [a,b] : [b,a];
        return [[[l[0],-1],[r[0],-1],[r[0],r[1]+0.05],[l[0],l[1]+0.05]]];
      }));
      const cutter = transform(keep(Manifold.extrude(envelope, w + 2)), [0,0,1], [0,1,0], [1,0,0], [-1,0,0]);
      const jamb = transform(keep(Manifold.extrude(ccw(frame.profileMm), h - stockThickness)), [1,0,0], [0,0,1], [0,1,0]);
      const left = keep(jamb.subtract(cutter));
      const rightBase = transform(jamb, [-1,0,0], [0,1,0], [0,0,1], [w,0,0]);
      const right = keep(rightBase.subtract(cutter));
      const headerBase = transform(keep(Manifold.extrude(ccw(frame.profileMm), w)), [0,-1,0], [0,0,1], [1,0,0], [0,h,0]);
      const headStock = keep(headerBase.trimByPlane([0,1,0], h - stockThickness));
      const headRebate = keep(keep(headerBase.trimByPlane([1,0,0], maxInset))
        .trimByPlane([-1,0,0], maxInset - w));
      const header = keep(headStock.add(headRebate));
      [[left,'left','paint'],[right,'right','paint'],[header,'head','paint'],[sill,'threshold',spec.material]]
        .forEach(([solid, name, material]) => {
          const geometry = geometryOf(solid);
          output.push({ name, material, geometry });
          if (material === 'alu') return;
          const mapping = name === 'threshold' ? { lengthAxis: 'x', sectionAxes: ['z','y'], repeatsPerMetre: material === 'oak' ? 2 : 1,
            grainAxis: material === 'oak' ? 'u' : 'v' }
            : name === 'head' ? { lengthAxis: 'x', sectionAxes: ['y','z'], originMm: [h,0], signs: [-1,1] }
            : name === 'right' ? { originMm: [w,0], signs: [-1,1] } : {};
          mapExtrudedWoodUVs(geometry, name === 'threshold' ? spec.profiles : [frame.profileMm], mapping);
        });
      cache.set(key, output.map((p) => ({ ...p, geometry: p.geometry.clone() })));
      if (cache.size > 12) { const first = cache.keys().next().value; cache.get(first).forEach((p) => p.geometry.dispose()); cache.delete(first); }
      return output;
    } catch (error) { output.forEach((p) => p.geometry.dispose()); throw error; }
    finally { owned.reverse().forEach((s) => s.delete()); }
  };
}
